import type { ProjectLifecycle, WorkOrderState } from "@szyos/contracts";
import { orderActivity } from "./activity";

/**
 * Projektübersicht (Portfolio): Stand je Projekt aus echten Daten, ohne Prozentangaben (Gestaltungsregel 1).
 * Die Lebensphase setzt der Mensch, den aktuellen Stand leitet szyOS aus den Aufträgen ab.
 */

/**
 * `working` nur mit belegter Arbeit (aktiver Lauf oder Prüfung durch szyOS), `waiting` für Aufträge in der
 * Warteschlange oder Anbieterpause, `held` für angehaltene Aufträge.
 */
export type ProjectActivity = "needs_you" | "working" | "waiting" | "held" | "quiet";

/** Zustände, in denen ein Mensch handeln muss: Freigabe, Start, Entscheidung, Review, Release, Blockade. */
const NEEDS_YOU: readonly WorkOrderState[] = [
  "DRAFT",
  "SPEC_REVIEW",
  "PLAN_REVIEW",
  "READY",
  "WAITING_HUMAN",
  "REVIEW_READY",
  "RELEASE_APPROVAL",
  "BLOCKED",
  "SUSPECT",
];
/** Abgeschlossene Aufträge zählen nicht mehr als offen. */
const CLOSED: readonly WorkOrderState[] = ["RELEASED", "CANCELLED", "FAILED"];

export interface OrderFacts {
  state: WorkOrderState;
  openDecisions: number;
  itemsDone: number;
  itemsTotal: number;
  origin: string;
  updatedAt: string;
  heldAt?: string | null;
  /** Aktive Läufe; ohne Angabe gilt keiner als aktiv. */
  activeRuns?: number;
  queuedRuns?: number;
  /** Offene Schritte beim Owner; ein ausgelieferter Auftrag mit solchen Schritten braucht dich noch. */
  openOwnerSteps?: number;
}

export interface ProjectOverview {
  activity: ProjectActivity;
  openOrders: number;
  openItems: number;
  openDecisions: number;
  /** Offene Aufträge, die aus taskFLOW kamen. */
  fromTaskflow: number;
  lastActivityAt: string;
}

export function isOrderOpen(state: WorkOrderState): boolean {
  return !CLOSED.includes(state);
}

export function projectOverview(projectCreatedAt: string, orders: readonly OrderFacts[]): ProjectOverview {
  const open = orders.filter((o) => isOrderOpen(o.state));
  const openDecisions = open.reduce((n, o) => n + o.openDecisions, 0);
  const activities = open.map((o) =>
    orderActivity({
      state: o.state,
      heldAt: o.heldAt ?? null,
      activeRuns: o.activeRuns ?? 0,
      queuedRuns: o.queuedRuns ?? 0,
    }),
  );
  const activity: ProjectActivity =
    openDecisions > 0 ||
    open.some((o) => NEEDS_YOU.includes(o.state)) ||
    orders.some((o) => (o.openOwnerSteps ?? 0) > 0)
      ? "needs_you"
      : activities.includes("working")
        ? "working"
        : activities.includes("queued") || activities.includes("idle")
          ? "waiting"
          : activities.includes("held")
            ? "held"
            : "quiet";
  const times = [projectCreatedAt, ...orders.map((o) => o.updatedAt)].map((t) => new Date(t).getTime());
  return {
    activity,
    openOrders: open.length,
    openItems: open.reduce((n, o) => n + Math.max(0, o.itemsTotal - o.itemsDone), 0),
    openDecisions,
    fromTaskflow: open.filter((o) => o.origin === "taskflow").length,
    lastActivityAt: new Date(Math.max(...times)).toISOString(),
  };
}

/** Ein Projekt gilt erst als abgeschlossen, wenn kein Auftrag mehr offen ist. */
export function canCloseProject(openOrders: number): boolean {
  return openOrders === 0;
}

/**
 * Gruppen der Projektliste (24.10.2026, statt sieben Filtern): Jedes Projekt steht in genau einer Gruppe, in dieser
 * Reihenfolge. Fokus und „Braucht dich“ zuerst und offen; Ruhiges, Betrieb und Abgeschlossenes zugeklappt.
 */
export const PORTFOLIO_GROUPS = ["focus", "needs_you", "running", "parked", "operating", "closed"] as const;
export type PortfolioGroup = (typeof PORTFOLIO_GROUPS)[number];

/** Gruppen, die beim Öffnen der Liste aufgeklappt sind. */
export const OPEN_PORTFOLIO_GROUPS: ReadonlySet<PortfolioGroup> = new Set(["focus", "needs_you", "running"]);

export function portfolioGroup(
  lifecycle: ProjectLifecycle,
  activity: ProjectActivity,
  focus = false,
): PortfolioGroup {
  if (lifecycle === "closed") return "closed";
  if (focus) return "focus";
  if (activity === "needs_you") return "needs_you";
  if (activity === "working" || activity === "waiting" || activity === "held") return "running";
  return lifecycle === "operating" ? "operating" : "parked";
}

const ACTIVITY_RANK: Record<ProjectActivity, number> = {
  needs_you: 0,
  working: 1,
  waiting: 2,
  held: 3,
  quiet: 4,
};

/** Reihenfolge: zuerst, was dich braucht, dann laufende Arbeit, dann ruhige Projekte; jeweils neueste zuerst. */
export function comparePortfolio(a: ProjectOverview, b: ProjectOverview): number {
  return (
    ACTIVITY_RANK[a.activity] - ACTIVITY_RANK[b.activity] ||
    new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
  );
}
